import {
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FilterButton,
  FormTab,
  FunctionField,
  List,
  NumberInput,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import { useCommonLabels } from "../i18n/hooks";
import { FilePreviewLink } from "../ui/fileUpload";
import { FormSection } from "../ui/forms/FormSection";
import { TitleLangFields } from "../ui/forms/langFields";
import { LangTabbedForm } from "../ui/forms/LangTabbedForm";
import { LocalizedFileControls, LocalizedFileLangField } from "../ui/forms/LocalizedFileInput";
import { normalizeContentRecord } from "../ui/forms/normalizeMedia";
import CoverTextColorField from "../ui/forms/CoverTextColorField";

const ACTIVE_FILTER_CHOICES = [
  { id: true, name: "Aktiv" },
  { id: false, name: "Deaktiv" },
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
  </TopToolbar>
);

const SECTIONS = [
  { source: "Description", label: "Описание" },
  { source: "Reasons", label: "Причины" },
  { source: "Symptoms", label: "Симптомы" },
  { source: "Diagnostics", label: "Диагностика" },
  { source: "Treatment", label: "Лечение" },
  { source: "Danger", label: "Опасность" },
  { source: "GroupOfRisk", label: "Группа риска" },
  { source: "Prevention", label: "Профилактика" },
] as const;

const CatalogLangTab = ({
  lang,
  titleRequired,
}: {
  lang: "AZ" | "RU" | "EN";
  titleRequired?: boolean;
}) => (
  <>
    <TitleLangFields lang={lang} titleRequired={titleRequired} />
    {SECTIONS.map(({ source, label }) => (
      <TextInput
        key={source}
        source={`${source}${lang}`}
        label={label}
        fullWidth
        multiline
        rows={4}
      />
    ))}
    <LocalizedFileLangField baseSource="Image" lang={lang} label="Обложка" kind="image" />
  </>
);

const PhCatalogForm = () => {
  const c = useCommonLabels();

  return (
    <LangTabbedForm>
      <FormTab label="Общее">
        <FormSection title="Справочник" subtitle="Связь с legacy TopDoktor и порядок сортировки">
          <NumberInput source="SortOrder" label="Порядок" defaultValue={0} fullWidth />
          <NumberInput
            source="LegacyDiseaseId"
            label="ID болезни (legacy TopDoktor / Strapi)"
            fullWidth
            helperText="Связь со справочником videodoktor.az для фильтра врачей в приложении"
          />
          <BooleanInput source="Active" label={c.active} defaultValue />
        </FormSection>
        <FormSection title="Обложка">
          <LocalizedFileControls
            baseSource="Image"
            label="Обложка (cover)"
            kind="image"
            helperText="JPG/PNG — отображается в списке Mənbə"
          />
          <CoverTextColorField />
        </FormSection>
      </FormTab>
      <FormTab label="🇦🇿 AZ">
        <CatalogLangTab lang="AZ" titleRequired />
      </FormTab>
      <FormTab label="🇷🇺 RU">
        <CatalogLangTab lang="RU" titleRequired />
      </FormTab>
      <FormTab label="🇬🇧 EN">
        <CatalogLangTab lang="EN" titleRequired />
      </FormTab>
    </LangTabbedForm>
  );
};

export const PhCatalogList = () => {
  const c = useCommonLabels();
  const filters = [
    <SearchInput key="q" source="q" placeholder={`${c.search} (название, симптомы)`} resettable />,
    <SelectInput
      key="active"
      source="active"
      label={c.status}
      choices={ACTIVE_FILTER_CHOICES}
      emptyText="Hamısı"
    />,
  ];

  return (
    <List
      filters={filters}
      actions={<ListActions />}
      sort={{ field: "SortOrder", order: "DESC" }}
      perPage={50}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="Id" label="ID" />
        <TextField source="TitleAZ" label="Название (AZ)" />
        <TextField source="TitleRU" label="Название (RU)" />
        <FunctionField
          label={c.image}
          render={(record: { Image?: string }) => <FilePreviewLink value={record.Image} />}
        />
        <TextField source="LegacyDiseaseId" label="Legacy ID" />
        <BooleanField source="Active" label={c.status} />
      </Datagrid>
    </List>
  );
};

export const PhCatalogEdit = () => (
  <Edit mutationMode="pessimistic" transform={normalizeContentRecord}>
    <PhCatalogForm />
  </Edit>
);

export const PhCatalogCreate = () => (
  <Create transform={normalizeContentRecord}>
    <PhCatalogForm />
  </Create>
);
